import { TriangleAlert } from 'lucide-react'

import { logoUrl } from '@/assets/asset-urls'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { clearTokenData } from '@/lib/token/token'
import { CONTACT } from '@/utils/constants'

/**
 * Error-boundary fallback for a fault that is not an auth failure: a 403, a 5xx while api deploys,
 * a render error. A 401 never reaches here; the boundary's onError sends that one to /login.
 *
 * It navigates nowhere on its own. /login is a Keycloak shim, so a live SSO session signs the user
 * straight back into the same fault, and that loop is why this page exists. Both actions are
 * therefore user-driven: retry reloads, and sign out ends the Keycloak session first.
 */

const HEADING = 'We encountered an issue'
const BODY =
  "You're still signed in, but something went wrong. If this error persists, please contact customer support."

export function AppFault() {
  const retry = (): void => {
    window.location.reload()
  }

  // The redirect cannot reach localStorage and the /login shim reads it, so drop the tokens first.
  const signOut = (): void => {
    clearTokenData()

    window.location.href = `${import.meta.env.VITE_USER_MANAGER_URL}/auth/oidc/logout`
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10 bg-[#022D4F]">
      <div className="w-full max-w-md flex flex-col items-center gap-8">
        <img src={logoUrl} alt="VouchedFor" />

        <Card className="w-full">
          <CardContent className="flex flex-col items-center gap-4 px-8 py-10 text-center">
            <TriangleAlert className="size-12 text-primary" strokeWidth={1.5} aria-hidden />

            <h1 className="text-2xl font-bold text-[#022D4F]">{HEADING}</h1>

            <p className="text-muted-foreground">{BODY}</p>

            <div className="mt-2 flex flex-col gap-1">
              <p>
                Call us:{' '}
                <a className="font-bold text-primary" href={`tel:${CONTACT.PHONE_NUMBER.replace(/\s/g, '')}`}>
                  {CONTACT.PHONE_NUMBER}
                </a>
              </p>
              <p>
                Email us:{' '}
                <a className="font-bold text-primary" href={`mailto:${CONTACT.EMAIL}`}>
                  {CONTACT.EMAIL}
                </a>
              </p>
            </div>

            <div className="mt-4 flex gap-3">
              <Button onClick={retry}>Try again</Button>

              <Button variant="outline" onClick={signOut}>
                Sign out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
